import React, { createContext, useContext, useEffect, useState } from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { createApolloClients } from '../graphql/client-v2';

// Define the context type
interface SubgraphContextType {
    clients: Record<string, ApolloClient<NormalizedCacheObject>>;
    loading: boolean;
    error: Error | null;
    chainId: string;
    setChainId: (chainId: string) => void;
    disabled: boolean;
}

// Create the context with default values
const SubgraphContext = createContext<SubgraphContextType>({
    clients: {},
    loading: true,
    error: null,
    chainId: '31337',
    setChainId: () => { },
    disabled: false
});

// Props for the provider component
interface SubgraphProviderProps {
    initialChainId?: string;
    children: React.ReactNode;
}

// Provider component that wraps the app
export const SubgraphProvider: React.FC<SubgraphProviderProps> = ({
    initialChainId = '8453',
    children
}) => {
    const [chainId, setChainId] = useState(initialChainId);
    const [clients, setClients] = useState<Record<string, ApolloClient<NormalizedCacheObject>>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Check if subgraphs are disabled via environment variable
    const disabled = import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true';

    // Initialize or update clients when chainId changes
    useEffect(() => {
        const initClients = async () => {
            if (disabled) {
                console.log('🚫 SubgraphContext: Subgraphs disabled via VITE_DISABLE_SUBGRAPHS, skipping initialization');
                setLoading(false);
                setClients({});
                setError(null);
                return;
            }

            try {
                console.log(`SubgraphContext: Initializing clients for chain ID ${chainId}`);
                setLoading(true);
                const newClients = await createApolloClients(chainId);
                console.log('SubgraphContext: Clients created successfully:', Object.keys(newClients));
                setClients(newClients);
                setError(null);
            } catch (err) {
                console.error('SubgraphContext: Error initializing Apollo clients:', err);
                setError(err instanceof Error ? err : new Error(String(err)));
            } finally {
                setLoading(false);
            }
        };

        initClients();
    }, [chainId, disabled]);

    return (
        <SubgraphContext.Provider
            value={{
                clients,
                loading,
                error,
                chainId,
                setChainId,
                disabled
            }}
        >
            {children}
        </SubgraphContext.Provider>
    );
};

// Custom hook to use the subgraph context
export const useSubgraph = () => useContext(SubgraphContext);

// Specialized hooks for specific subgraphs
export const useTimelockSubgraph = () => {
    const { clients, loading, error, chainId, disabled } = useSubgraph();

    if (disabled) {
        return {
            client: null,
            loading: false,
            error: null,
            disabled: true
        };
    }

    if (!clients.timelockTracker) {
        console.warn(`No timelockTracker client available for chain ID ${chainId}`);
    }

    return {
        client: clients.timelockTracker,
        loading: loading || !clients.timelockTracker,
        error: error || (!clients.timelockTracker && !loading ? new Error(`No timelockTracker client available for chain ID ${chainId}`) : null),
        disabled: false
    };
};

export const useCoreSubgraph = () => {
    const { clients, loading, error, disabled } = useSubgraph();

    if (disabled) {
        return {
            client: null,
            loading: false,
            error: null,
            disabled: true
        };
    }

    return {
        client: clients.coreTracker,
        loading: loading || !clients.coreTracker,
        error,
        disabled: false
    };
};

export const useFeaturesSubgraph = () => {
    const { clients, loading, error, disabled } = useSubgraph();

    if (disabled) {
        return {
            client: null,
            loading: false,
            error: null,
            disabled: true
        };
    }

    return {
        client: clients.featuresTracker,
        loading: loading || !clients.featuresTracker,
        error,
        disabled: false
    };
};

export const useControllerSubgraph = () => {
    const { clients, loading, error, disabled } = useSubgraph();

    if (disabled) {
        return {
            client: null,
            loading: false,
            error: null,
            disabled: true
        };
    }

    return {
        client: clients.controllerTracker,
        loading: loading || !clients.controllerTracker,
        error,
        disabled: false
    };
};
